import type { Timezone } from '@/bindings/Timezone'

// Both spellings are IANA-valid (chrono-tz links them); runtimes disagree on
// which one they list, so the default tries each.
const LAUNCH_MARKET: readonly Timezone[] = ['Asia/Ho_Chi_Minh', 'Asia/Saigon']

let cached: readonly Timezone[] | undefined

/** IANA zone names known to this runtime; the backend validates the pick (E1). */
export function timezoneOptions(): readonly Timezone[] {
  if (!cached) {
    try {
      cached = Intl.supportedValuesOf('timeZone')
    } catch {
      cached = LAUNCH_MARKET
    }
  }
  return cached
}

/** The device's zone when the runtime lists it, else the launch market's. */
export function defaultTimezone(): Timezone {
  const options = timezoneOptions()
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (options.includes(zone)) return zone
  } catch {
    // Unresolvable locale data: fall through.
  }
  return LAUNCH_MARKET.find((zone) => options.includes(zone)) ?? options[0] ?? LAUNCH_MARKET[0]!
}

/** The zone's current UTC offset as "GMT+07:00" ("GMT" at zero); `undefined` when the runtime cannot say. */
export function timezoneOffset(zone: Timezone, at: Date = new Date()): string | undefined {
  try {
    const part = new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')
    return part?.value
  } catch {
    return undefined
  }
}
